import { useCallback, useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import TextField from "@mui/material/TextField";
import { confirmAlert } from "react-confirm-alert";
import dayjs from "dayjs";

import TextFieldDate from "../../UI/TextFieldDate";
import Select from "../../UI/Select";
import LoadingDialog from "../../UI/LoadingDialog";
import { errorMessage, successMessage } from "../../../util/ConfirmAlertUtil";
import {
  serviceExcludeByDate,
  serviceIncludeByDate,
  serviceIncludeByDateV2,
} from "../api-presenca";
import {
  serviceUpdateByCodAlunoAndAnoLetivo,
  serviceUpdateByDate,
  serviceUpdateByDateAndLegendaDiario,
  serviceUpdateByDateAndTurma,
} from "../../PresencaAluno/api-presenca-aluno";
import AlunoFindDialog from "../../Aluno/AlunoFindDialog";
import session from "../../../SessionHandler/session-helper";
import { findByAnoLetivo } from "../../Turma/api-turma";
import { findLegendaDiarioAll } from "../../LegendaDiario/api-legenda-diario";

/**
 * Cada serviço em lote é descrito por um objeto de configuração; o card
 * genérico (`ServiceCard`) cuida de estado, validação, confirmação e
 * loading. Para adicionar um novo serviço basta incluir um item aqui.
 *
 * Campos do card: data (padrão, desligue com `needsData: false`), turma
 * (`needsTurma`), legenda diário (`needsLegendaDiario`) e aluno (`needsAluno`).
 *
 * `run` recebe `{ data, codTurma, codLegendaDiario, aluno }`, com `data` já
 * como `Date` (as APIs de presença-aluno chamam `getTime()` direto).
 */
const SERVICES = [
  {
    key: "inclusao",
    icon: EventAvailableOutlinedIcon,
    title: "Inclusão de presença por data",
    description: "Inclui as presenças conforme o cronograma de aula.",
    actionLabel: "Incluir presenças",
    confirm: "Deseja iniciar o serviço de inclusão de presença por data?",
    loading: "Serviço de inclusão de presença por data em andamento...",
    success: "Serviço de inclusão de datas realizado com sucesso.",
    run: ({ data }) => serviceIncludeByDate(data),
  },
  {
    key: "inclusaoV2",
    icon: PlaylistAddOutlinedIcon,
    title: "Inclusão de presença por data (todas as disciplinas)",
    description: "Inclui as presenças de todas as disciplinas.",
    warning:
      "Atenção: as presenças serão criadas para todas as disciplinas, independentemente do cronograma.",
    color: "warning",
    actionLabel: "Incluir presenças",
    confirm:
      "Deseja iniciar o serviço de inclusão de presença por data (todas as disciplinas)?",
    loading: "Serviço de inclusão de presença por data em andamento...",
    success: "Serviço de inclusão de datas realizado com sucesso.",
    run: ({ data }) => serviceIncludeByDateV2(data),
  },
  {
    key: "exclusao",
    icon: DeleteSweepOutlinedIcon,
    title: "Exclusão de presença por data",
    description: "Remove as presenças da data selecionada.",
    warning: "Esta ação não pode ser desfeita.",
    color: "error",
    actionLabel: "Excluir presenças",
    confirm: "Deseja iniciar o serviço de exclusão de presença por data?",
    loading: "Serviço de exclusão de presença por data em andamento...",
    success: "Serviço de exclusão está acontecendo em segundo plano.",
    run: ({ data }) => serviceExcludeByDate(data),
  },
  {
    key: "atualizacao",
    icon: SyncOutlinedIcon,
    title: "Atualização de presença aluno por data",
    description: "Recalcula presente/falta dos alunos na data.",
    actionLabel: "Atualizar presença aluno",
    confirm:
      "Deseja iniciar o serviço de atualização de presença aluno por data?",
    loading:
      "Serviço de atualização de presença aluno (presente/falta) por data em andamento...",
    success: "Serviço de atualização de datas realizado com sucesso.",
    run: ({ data }) => serviceUpdateByDate(data),
  },
  {
    key: "atualizacaoTurma",
    icon: GroupsOutlinedIcon,
    title: "Atualização de presença aluno por data e turma",
    description: "Recalcula presente/falta dos alunos de uma turma na data.",
    needsTurma: true,
    actionLabel: "Atualizar presença aluno",
    confirm:
      "Deseja iniciar o serviço de atualização de presença aluno por data e turma?",
    loading:
      "Serviço de atualização de presença aluno (presente/falta) por data e turma em andamento...",
    success: "Serviço de atualização de data e turma realizado com sucesso.",
    run: ({ data, codTurma }) => serviceUpdateByDateAndTurma(data, codTurma),
  },
  {
    key: "atualizacaoLegenda",
    icon: LabelOutlinedIcon,
    title: "Atualização de presença aluno por data e legenda diário",
    description: "Aplica a legenda do diário às presenças dos alunos na data.",
    needsLegendaDiario: true,
    actionLabel: "Atualizar presença aluno",
    confirm:
      "Deseja iniciar o serviço de atualização de presença aluno por data e legenda diário?",
    loading:
      "Serviço de atualização de presença aluno por data e legenda diário em andamento...",
    success:
      "Serviço de atualização de data e legenda diário realizado com sucesso.",
    run: ({ data, codLegendaDiario }) =>
      serviceUpdateByDateAndLegendaDiario(data, codLegendaDiario),
  },
  {
    key: "atualizacaoAluno",
    icon: PersonSearchOutlinedIcon,
    title: "Atualização de presença aluno por aluno e ano letivo",
    description: `Recalcula presente/falta de um aluno em todo o ano letivo atual (${session.getAnoLetivo()}).`,
    needsData: false,
    needsAluno: true,
    actionLabel: "Atualizar presença aluno",
    confirm:
      "Deseja iniciar o serviço de atualização de presença aluno por aluno e ano letivo?",
    loading:
      "Serviço de atualização de presença aluno por aluno e ano letivo em andamento...",
    success:
      "Serviço de atualização de presença por aluno e ano letivo realizado com sucesso.",
    run: ({ aluno }) => serviceUpdateByCodAlunoAndAnoLetivo(aluno.codigo),
  },
];

const toOptions = (list) =>
  list.map((item) => ({ value: item.codigo, description: item.descricao }));

/** Carrega uma lista de opções para Select, avisando erros da API. */
function useOptions(fetcher) {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let active = true;
    fetcher()
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setOptions(toOptions(data));
        }
      })
      .catch((err) => console.error(err));
    return () => {
      active = false;
    };
  }, [fetcher]);

  return options;
}

function ServiceCard({ service, turmaOptions, legendaDiarioOptions }) {
  const [data, setData] = useState(null);
  const [codTurma, setCodTurma] = useState("");
  const [codLegendaDiario, setCodLegendaDiario] = useState("");
  const [aluno, setAluno] = useState(null);
  const [openAlunoSearch, setOpenAlunoSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const Icon = service.icon;
  const color = service.color || "primary";
  const needsData = service.needsData !== false;
  const canSubmit =
    (!needsData || (!!data && data.isValid())) &&
    (!service.needsTurma || codTurma !== "") &&
    (!service.needsLegendaDiario || codLegendaDiario !== "") &&
    (!service.needsAluno || !!aluno);

  const execute = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await service.run({
        data: data ? data.toDate() : null,
        codTurma,
        codLegendaDiario,
        aluno,
      });
      if (result?.error) {
        errorMessage(result.message);
      } else if (result) {
        successMessage(service.success);
      }
    } catch (err) {
      console.error(err);
      errorMessage("Ocorreu um erro ao executar o serviço. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }, [service, data, codTurma, codLegendaDiario, aluno]);

  const handleClick = () => {
    const confirmDetails = [
      needsData && `Data: ${data.format("DD/MM/YYYY")}`,
      service.needsAluno && `Aluno: ${aluno.nome}`,
    ]
      .filter(Boolean)
      .join(" | ");

    confirmAlert({
      title: "Confirmação",
      message: `${service.confirm} (${confirmDetails})`,
      buttons: [
        { label: "Sim", onClick: execute },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTop: 3,
        borderTopColor: `${color}.main`,
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
          <Box
            sx={{
              display: "flex",
              p: 1,
              borderRadius: 2,
              bgcolor: `${color}.main`,
              color: `${color}.contrastText`,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
              {service.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {service.description}
            </Typography>
          </Box>
        </Stack>

        {service.warning && (
          <Alert severity={color === "error" ? "error" : "warning"} sx={{ mb: 2 }}>
            {service.warning}
          </Alert>
        )}

        <Stack spacing={2}>
          {needsData && (
            <TextFieldDate
              required
              label="Data"
              value={data}
              onChange={(value) => setData(value ? dayjs(value) : null)}
              disabled={isLoading}
              slotProps={{ textField: { fullWidth: true } }}
            />
          )}
          {service.needsAluno && (
            <>
              <AlunoFindDialog
                openSearch={openAlunoSearch}
                setOpenSearch={setOpenAlunoSearch}
                setAluno={setAluno}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Aluno"
                  required
                  fullWidth
                  value={aluno?.nome ?? ""}
                  placeholder="Nenhum aluno selecionado"
                  slotProps={{
                    input: { readOnly: true },
                    inputLabel: { shrink: true },
                  }}
                />
                <Button
                  variant="outlined"
                  color={color}
                  disabled={isLoading}
                  onClick={() => setOpenAlunoSearch(true)}
                  startIcon={<PersonSearchOutlinedIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  Buscar
                </Button>
              </Stack>
            </>
          )}
          {service.needsTurma && (
            <Select
              label="Turma"
              value={codTurma}
              list={turmaOptions}
              onChange={(event) => setCodTurma(event.target.value)}
            />
          )}
          {service.needsLegendaDiario && (
            <Select
              label="Legenda Diário"
              value={codLegendaDiario}
              list={legendaDiarioOptions}
              onChange={(event) => setCodLegendaDiario(event.target.value)}
            />
          )}
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color={color}
          disabled={!canSubmit || isLoading}
          onClick={handleClick}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {service.actionLabel}
        </Button>
      </CardActions>

      {isLoading && (
        <LoadingDialog
          openDialog={isLoading}
          setOpenDialog={setIsLoading}
          title="Em processo"
          description={service.loading}
        />
      )}
    </Card>
  );
}

export default function PresencaLoteContainer() {
  const turmaOptions = useOptions(findByAnoLetivo);
  const legendaDiarioOptions = useOptions(findLegendaDiarioAll);

  return (
    <Container component={Paper} sx={{ p: { xs: 2, md: 3 } }}>
      <Box mb={3}>
        <Typography variant="h6">Presença Lote</Typography>
        <Typography variant="body2" color="text.secondary">
          Serviços para incluir, excluir e atualizar presenças em lote por
          data.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {SERVICES.map((service) => (
          <Grid key={service.key} size={{ xs: 12, md: 6, lg: 4 }}>
            <ServiceCard
              service={service}
              turmaOptions={turmaOptions}
              legendaDiarioOptions={legendaDiarioOptions}
            />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
